import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatEngineLabel } from "@/lib/format";
import type { CiRunDetail, CiTestResult } from "@/lib/types";
import { cn } from "@/lib/utils";
import { formatDurationMs, timelineBarColor } from "./timeline-shared";

/**
 * Per-machine Gantt timeline showing which test ran on each machine and when.
 * Derives start times from created_at - duration_seconds.
 */

interface TestBar {
  result: CiTestResult;
  startMs: number;
  durationMs: number;
  leftPercent: number;
  widthPercent: number;
}

interface MachineLane {
  machineName: string;
  bars: TestBar[];
}

interface RawBar {
  result: CiTestResult;
  startMs: number;
  endMs: number;
}

function buildTimeline(run: CiRunDetail): {
  lanes: MachineLane[];
  totalMs: number;
  runStartMs: number;
} {
  const runStart = run.started_at ? new Date(run.started_at).getTime() : 0;

  // Group non-skipped results by machine
  const machineResults = new Map<string, CiTestResult[]>();
  for (const result of run.test_results) {
    if (result.status === "skip") continue;
    const rawName = result.machine_name || "unknown";
    // Cross-restore uses "machineA → machineB" — place on both lanes
    const machineNames = rawName.includes(" → ") ? rawName.split(" → ") : [rawName];
    for (const machineName of machineNames) {
      const existing = machineResults.get(machineName) ?? [];
      existing.push(result);
      machineResults.set(machineName, existing);
    }
  }

  // Check if results have timestamps (new runs do, old ones don't)
  const hasTimestamps = run.test_results.some((result) => result.started_at || result.completed_at);
  const nowMs = Date.now();

  // First pass: compute absolute bar positions so the timeline span can be derived
  // from the last test end — not from run.completed_at, which can trail well past
  // the last test (teardown, cleanup, unset on in-progress runs).
  const rawLanes = new Map<string, RawBar[]>();
  let maxEndMs = 0;

  if (hasTimestamps) {
    // Precise placement using started_at / completed_at
    for (const [machineName, results] of machineResults) {
      const bars: RawBar[] = [];
      for (const result of results) {
        let startMs: number;
        let endMs: number;

        if (result.started_at) {
          startMs = Math.max(new Date(result.started_at).getTime() - runStart, 0);
          if (result.completed_at) {
            endMs = new Date(result.completed_at).getTime() - runStart;
          } else {
            // In-progress: bar extends to now
            endMs = nowMs - runStart;
          }
        } else if (result.completed_at) {
          // Old format: only completed_at, derive start from duration
          endMs = new Date(result.completed_at).getTime() - runStart;
          startMs = Math.max(endMs - result.duration_seconds * 1000, 0);
        } else {
          continue;
        }

        bars.push({ result, startMs, endMs });
        if (endMs > maxEndMs) maxEndMs = endMs;
      }
      if (bars.length > 0) rawLanes.set(machineName, bars);
    }
  } else {
    // Fallback: stack tests sequentially per machine in result order
    for (const [machineName, results] of machineResults) {
      let cursor = 0;
      const bars: RawBar[] = [];
      for (const result of results) {
        const durationMs = result.duration_seconds * 1000;
        const endMs = cursor + durationMs;
        bars.push({ result, startMs: cursor, endMs });
        if (endMs > maxEndMs) maxEndMs = endMs;
        cursor = endMs;
      }
      if (bars.length > 0) rawLanes.set(machineName, bars);
    }
  }

  const totalMs = maxEndMs;
  if (totalMs <= 0) return { lanes: [], totalMs: 0, runStartMs: runStart };

  // Second pass: convert to percentage-based bars now that totalMs is known
  const lanes: MachineLane[] = [];
  for (const [machineName, rawBars] of rawLanes) {
    const bars: TestBar[] = rawBars.map((rawBar) => {
      const durationMs = Math.max(rawBar.endMs - rawBar.startMs, 0);
      return {
        result: rawBar.result,
        startMs: rawBar.startMs,
        durationMs,
        leftPercent: (rawBar.startMs / totalMs) * 100,
        widthPercent: Math.max((durationMs / totalMs) * 100, 0.3),
      };
    });
    bars.sort((first, second) => first.startMs - second.startMs);
    lanes.push({ machineName, bars });
  }
  // Sort lanes by machine name
  lanes.sort((first, second) => first.machineName.localeCompare(second.machineName));

  return { lanes, totalMs, runStartMs: runStart };
}

function TimelineBar({ bar, runId }: { bar: TestBar; runId: string }) {
  const navigate = useNavigate();
  const isRunning = bar.result.started_at && !bar.result.completed_at;
  const statusKey = isRunning ? "in_progress" : bar.result.status;
  const color = timelineBarColor(statusKey);
  const base = `${bar.result.test_name} / ${bar.result.version}`;
  const label = bar.result.engine_version
    ? `${base} | ${formatEngineLabel(bar.result.engine)} ${bar.result.engine_version}`
    : base;

  return (
    <button
      type="button"
      className={cn(
        "absolute top-0.5 h-5 rounded-sm cursor-pointer transition-opacity hover:opacity-80",
        "flex items-center overflow-hidden px-1 border-0",
        color,
        isRunning && "animate-pulse",
      )}
      style={{
        left: `${bar.leftPercent}%`,
        width: `${bar.widthPercent}%`,
        minWidth: "4px",
      }}
      title={`${label}\n${formatDurationMs(bar.durationMs)} | ${bar.result.status}`}
      onClick={(event) => {
        event.stopPropagation();
        navigate(`/ci/${runId}/tests/${bar.result.id}`);
      }}
    >
      {bar.widthPercent > 4 && (
        <span className="truncate text-[10px] leading-none text-white font-medium">
          {bar.result.test_name}
        </span>
      )}
    </button>
  );
}

function MachineLaneRow({ lane, runId }: { lane: MachineLane; runId: string }) {
  const totalDuration = lane.bars.reduce((sum, bar) => sum + bar.durationMs, 0);
  const passed = lane.bars.filter((bar) => bar.result.status === "pass").length;
  const failed = lane.bars.filter((bar) => bar.result.status === "fail").length;

  return (
    <div className="flex items-center gap-2 py-0.5">
      {/* Machine label */}
      <div className="w-44 shrink-0 flex flex-col">
        <span className="font-mono text-xs truncate">{lane.machineName}</span>
        <span className="text-[10px] text-muted-foreground">
          {lane.bars.length} tests | {formatDurationMs(totalDuration)}
          {failed > 0 && <span className="text-rose-500 ml-1">{failed} failed</span>}
          {failed === 0 && passed > 0 && <span className="text-emerald-500 ml-1">all passed</span>}
        </span>
      </div>
      {/* Timeline track */}
      <div className="relative flex-1 h-6">
        <div className="absolute inset-0 rounded-sm bg-muted/30" />
        {lane.bars.map((bar) => (
          <TimelineBar key={bar.result.id} bar={bar} runId={runId} />
        ))}
      </div>
    </div>
  );
}

function TimeAxis({ totalMs }: { totalMs: number }) {
  // Show 5 evenly-spaced time markers
  const markerCount = 5;
  const markers = Array.from({ length: markerCount + 1 }, (_, index) => {
    const fraction = index / markerCount;
    return {
      percent: fraction * 100,
      label: formatDurationMs(fraction * totalMs),
    };
  });

  return (
    <div className="flex items-center gap-2 pt-1">
      <div className="w-44 shrink-0" />
      <div className="relative flex-1 h-4">
        {markers.map((marker) => (
          <span
            key={marker.percent}
            className="absolute text-[10px] text-muted-foreground -translate-x-1/2"
            style={{ left: `${marker.percent}%` }}
          >
            {marker.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function MachineTimeline({ run }: { run: CiRunDetail }) {
  const [collapsed, setCollapsed] = useState(false);
  const { lanes, totalMs } = useMemo(() => buildTimeline(run), [run]);

  if (lanes.length === 0) return null;

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronRight className="size-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground" />
          )}
          <span className="text-sm font-semibold">Machine Timeline</span>
          <span className="text-xs text-muted-foreground">
            {lanes.length} machine{lanes.length !== 1 ? "s" : ""}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">{collapsed ? "Show" : "Hide"}</span>
      </button>
      {!collapsed && (
        <div className="px-4 pb-3 space-y-0.5">
          {lanes.map((lane) => (
            <MachineLaneRow key={lane.machineName} lane={lane} runId={run.run_id} />
          ))}
          <TimeAxis totalMs={totalMs} />
        </div>
      )}
    </div>
  );
}
